// A running QC job, placed on the service's stages (Queued → Scoring
// against the brief). Built only from video.status and video.stage.
// ScanCompact is the table-row version; ScanPanel is the result window's.
import React from 'react';
import AspectRatioOutlinedIcon from '@mui/icons-material/AspectRatioOutlined';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import BurstModeOutlinedIcon from '@mui/icons-material/BurstModeOutlined';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import GraphicEqRoundedIcon from '@mui/icons-material/GraphicEqRounded';
import HourglassEmptyRoundedIcon from '@mui/icons-material/HourglassEmptyRounded';
import { QC_STAGES, STATUS, stageIndex } from './qcFormat';
import { asText } from './qcClient';
import { AI, AiMark } from './aiUi';
import { C, Spinner } from './ui';

const STAGE_ICONS = [HourglassEmptyRoundedIcon, CloudDownloadOutlinedIcon, AspectRatioOutlinedIcon, BurstModeOutlinedIcon, GraphicEqRoundedIcon, FactCheckOutlinedIcon];

// One segment per stage: done, current (shimmering) or still to come. An
// unknown stage shows only "queued" as done; the stage text says the rest.
export function StageBar({ index, dark = false, height = 5 }) {
    const idle = dark ? 'rgba(255,255,255,0.16)' : '#e3e7f0';
    const done = dark ? C.gold : C.navy700;
    const reached = index < 0 ? 0 : index;
    return (
        <div aria-hidden="true" style={{ display: 'flex', gap: 3, width: '100%' }}>
            {QC_STAGES.map((stage, i) => {
                const current = index >= 0 && i === index;
                return (
                    <span key={stage} className={current ? 'vqc-seg-active' : undefined}
                        style={{ flex: 1, height, borderRadius: height, background: current ? undefined : (i < reached || (index < 0 && i === 0) ? done : idle) }} />
                );
            })}
        </div>
    );
}

export function ScanCompact({ status, stage }) {
    const label = (STATUS[status] || STATUS.processing).label;
    const stageText = asText(stage).trim();
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, width: 160 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, color: AI.ink }}>
                <AutoAwesomeIcon aria-hidden="true" className="vqc-twinkle" style={{ fontSize: 15, color: '#c79309' }} />
                <span>{label}</span>
            </div>
            <StageBar index={stageIndex(status, stage)} />
            {stageText && stageText !== label && <span style={{ fontSize: 12, color: C.muted }}>{stageText}</span>}
        </div>
    );
}

// Decorative: a frame being scanned, with sampled-frame tiles behind.
function ScanVisual() {
    return (
        <div aria-hidden="true" style={{ position: 'relative', width: 128, height: 188, borderRadius: 14, background: AI.panel, overflow: 'hidden', flexShrink: 0, boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.08)' }}>
            <div style={{ position: 'absolute', inset: 12, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
                {Array.from({ length: 9 }, (_, i) => (
                    <span key={i} style={{ borderRadius: 5, background: `rgba(255,255,255,${0.05 + (i % 3) * 0.03})`, border: '1px solid rgba(255,255,255,0.08)' }} />
                ))}
            </div>
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <AutoAwesomeIcon className="vqc-twinkle" style={{ fontSize: 34, color: C.gold }} />
            </div>
            <span className="vqc-scanline" />
        </div>
    );
}

function StepRow({ stage, i, index }) {
    const done = index >= 0 ? i < index : i === 0;
    const current = i === index;
    const Icon = STAGE_ICONS[i];
    let mark = <Icon aria-hidden="true" style={{ fontSize: 16, color: C.faint }} />;
    if (done) mark = <CheckCircleRoundedIcon aria-hidden="true" style={{ fontSize: 18, color: '#15803d' }} />;
    if (current) mark = <span style={{ color: '#c79309', display: 'inline-flex' }}><Spinner size={14} /></span>;
    return (
        <li aria-current={current ? 'step' : undefined}
            style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, fontWeight: current ? 700 : 600, color: done ? '#15803d' : current ? C.text : C.muted }}>
            <span style={{ width: 28, height: 28, borderRadius: 8, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, background: done ? '#e9f8ee' : current ? C.goldSoft : '#eef1f8' }}>
                {mark}
            </span>
            {stage}
        </li>
    );
}

// "AI review in progress", with every stage and where the run is now.
export function ScanPanel({ status, stage, hasPrevious = false }) {
    const index = stageIndex(status, stage);
    const now = asText(stage).trim() || (STATUS[status] || STATUS.queued).label;
    return (
        <div role="group" aria-label="QC progress" style={{ display: 'flex', gap: 22, alignItems: 'center', padding: 18, borderRadius: 16, background: AI.wash, border: `1px solid ${AI.washBorder}`, flexWrap: 'wrap' }}>
            <ScanVisual />
            <div style={{ flex: 1, minWidth: 260 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <AiMark size={30} active />
                    <div>
                        <div style={{ fontSize: 15, fontWeight: 800, color: C.text }}>AI review in progress</div>
                        <div role="status" style={{ fontSize: 12.5, color: C.muted, marginTop: 1 }}>Now: {now}</div>
                    </div>
                </div>
                <div style={{ margin: '14px 0 12px' }}><StageBar index={index} height={6} /></div>
                <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 8 }}>
                    {QC_STAGES.map((s, i) => <StepRow key={s} stage={s} i={i} index={index} />)}
                </ol>
                <p style={{ margin: '12px 0 0', fontSize: 12, color: C.muted }}>
                    This updates on its own. You can close this window and come back.
                    {hasPrevious && ' Below is the last result, until this run finishes.'}
                </p>
            </div>
        </div>
    );
}
